<template>
    <div class="m-profile-page">
      <div class="sticky-header">
        <FeedHeader />
      </div>
  
      <main class="m-profile-content">
        <button class="back-btn" @click="$router.back()">
          ← Назад
        </button>
  
        <div v-if="isLoadingProfile" class="skills-skeleton">
          <div class="skeleton title-skeleton"></div>
          <div class="skeleton card-skeleton"></div>
          <div class="skeleton tag-skeleton"></div>
          <div class="skeleton tag-skeleton short"></div>
          <div class="skeleton button-skeleton"></div>
        </div>
  
        <section v-else class="profile-skills-card">
          <h1 class="section-title">Ваши профессиональные скиллы</h1>
  
          <div class="hashtags-body">
            <HashtagsForProfile
              :initialHashtags="localHashtags"
              @save-hashtags="onInternalSave"
            />
          </div>
  
          <button
            class="save-btn"
            :disabled="!isChanged || isSaving"
            @click="save"
          >
            {{ isSaving ? 'Сохраняем…' : 'Сохранить' }}
          </button>
        </section>
      </main>
  
      <transition name="fade">
        <div v-if="showSnackbar" class="snackbar">{{ snackbarMessage }}</div>
      </transition>
  
      <MobileTabBar />
    </div>
  </template>
  
  <script setup>
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import FeedHeader from '../FeedHeader.vue';
  import MobileTabBar from '../MobileTabBar2.vue';
  import HashtagsForProfile from '../HashtagsForProfile.vue';
  import UserService from '../../api/UserService';
  
  const userId = localStorage.getItem('user_id');
  
  const user = ref({});
  const localHashtags = ref([]);
  const originalHashtags = ref([]);
  const isLoadingProfile = ref(true);
  const isSaving = ref(false);
  const showSnackbar = ref(false);
  const snackbarMessage = ref('');
  const snackbarType = ref('success');
  
  let snackbarTimer = null;
  
  const isChanged = computed(() => {
    return JSON.stringify(localHashtags.value) !== JSON.stringify(originalHashtags.value);
  });
  
  function showSnackbarMessage(message, type = 'success') {
    snackbarMessage.value = message;
    snackbarType.value = type;
    showSnackbar.value = true;
  
    clearTimeout(snackbarTimer);
    snackbarTimer = setTimeout(() => {
      showSnackbar.value = false;
    }, 3000);
  }
  
  function getLocalProfile() {
    try {
      return JSON.parse(localStorage.getItem('user_profile')) || {};
    } catch {
      return {};
    }
  }
  
  function syncSkillsFromProfile(profile) {
    const hashtags = Array.isArray(profile?.hashtags) ? profile.hashtags : [];
  
    localHashtags.value = [...hashtags];
    originalHashtags.value = [...hashtags];
  }
  
  function updateUserCache(patch) {
    user.value = {
      ...user.value,
      ...patch,
    };
  
    localStorage.setItem('user_profile', JSON.stringify(user.value));
  }
  
  async function loadProfile() {
    const startedAt = performance.now();
    isLoadingProfile.value = true;
  
    const cached = getLocalProfile();
  
    if (cached && Object.keys(cached).length) {
      user.value = cached;
      syncSkillsFromProfile(cached);
    }
  
    try {
      const fetched = await UserService.getProfile(userId);
      user.value = fetched || {};
      localStorage.setItem('user_profile', JSON.stringify(user.value));
      syncSkillsFromProfile(user.value);
    } catch (error) {
      console.error('Ошибка при загрузке профиля:', error);
      showSnackbarMessage('Не удалось загрузить скиллы', 'error');
  
      if (!Object.keys(user.value || {}).length) {
        syncSkillsFromProfile({ hashtags: [] });
      }
    } finally {
      const minSkeletonMs = 400;
      const elapsed = performance.now() - startedAt;
      const delay = Math.max(0, minSkeletonMs - elapsed);
  
      setTimeout(() => {
        isLoadingProfile.value = false;
      }, delay);
    }
  }
  
  function onInternalSave(newTags) {
    localHashtags.value = Array.isArray(newTags) ? [...newTags] : [];
  }
  
  async function save() {
    if (isSaving.value || !isChanged.value) return;
  
    isSaving.value = true;
  
    try {
      const payload = {
        user_id: userId,
        hashtags: localHashtags.value,
      };
  
      await UserService.updateUserProfile(payload);
  
      originalHashtags.value = [...localHashtags.value];
      updateUserCache({ hashtags: [...localHashtags.value] });
  
      showSnackbarMessage('Скиллы сохранены!');
    } catch (error) {
      console.error('Ошибка при сохранении скиллов:', error);
      showSnackbarMessage('Ошибка при сохранении скиллов 😢', 'error');
    } finally {
      isSaving.value = false;
    }
  }
  
  onMounted(() => {
    loadProfile();
  });
  
  onBeforeUnmount(() => {
    clearTimeout(snackbarTimer);
  });
  </script>
  
  <style scoped>
  .m-profile-page {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    background: var(--background-color);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .sticky-header {
    flex-shrink: 0;
  }
  
  .m-profile-content {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 16px;
    padding-bottom: 96px;
    box-sizing: border-box;
    -webkit-overflow-scrolling: touch;
  }
  
  .back-btn {
    height: 36px;
    padding: 0 14px;
    margin-bottom: 14px;
    border: none;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
    color: #111;
    font-size: 13px;
    font-family: 'Eastman-Medium', sans-serif;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }
  
  [data-theme='dark'] .back-btn {
    background: rgba(40, 40, 40, 0.92);
    color: #fff;
  }
  
  .profile-skills-card {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;
    border-radius: 24px;
    background: var(--sidebar-background);
    border: 1px solid #e6e6e6;
    font-family: 'Eastman-Regular', sans-serif;
  }
  
  [data-theme='dark'] .profile-skills-card {
    background: #2f2f2f;
    color: #eee;
    border-color: #3f3f3f;
  }
  
  .section-title {
    margin: 0 0 16px;
    font-size: 18px;
    line-height: 1.2;
    font-family: 'Eastman-Bold', sans-serif;
    letter-spacing: -0.4px;
    color: var(--text-primary);
  }
  
  .hashtags-body {
    padding: 14px;
    border-radius: 18px;
    background: var(--input-background);
    border: 1px solid #e2e2e2;
  }
  
  [data-theme='dark'] .hashtags-body {
    background: #3a3a3a;
    border-color: #555;
  }
  
  .save-btn {
    width: 100%;
    height: 50px;
    margin-top: 18px;
    background: #2173e5;
    color: white;
    font-family: 'Eastman-Medium', sans-serif;
    font-size: 15px;
    border-radius: 999px;
    border: none;
    cursor: pointer;
    box-shadow: 0 8px 24px rgba(33, 115, 229, 0.24);
    transition: 0.2s ease;
  }
  
  .save-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }
  
  .save-btn:hover:not(:disabled) {
    background: #195ec2;
  }
  
  .snackbar {
    position: fixed;
    left: 50%;
    bottom: calc(86px + env(safe-area-inset-bottom));
    transform: translateX(-50%);
    max-width: calc(100vw - 32px);
    background: #2173e5;
    color: white;
    padding: 10px 18px;
    border-radius: 14px;
    font-family: 'Eastman-Medium', sans-serif;
    font-size: 13px;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.22);
    z-index: 110;
  }
  
  .skills-skeleton {
    width: 100%;
    max-width: 430px;
    margin: 0 auto;
    padding: 20px;
    box-sizing: border-box;
    border-radius: 24px;
    background: var(--sidebar-background);
  }
  
  .skeleton {
    background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.2s infinite;
    border-radius: 14px;
  }
  
  [data-theme='dark'] .skeleton {
    background: linear-gradient(90deg, #333 25%, #444 50%, #333 75%);
    background-size: 200% 100%;
  }
  
  .title-skeleton {
    width: 74%;
    height: 24px;
  }
  
  .card-skeleton {
    width: 100%;
    height: 130px;
    margin-top: 18px;
  }
  
  .tag-skeleton {
    width: 70%;
    height: 42px;
    margin-top: 14px;
  }
  
  .tag-skeleton.short {
    width: 52%;
  }
  
  .button-skeleton {
    width: 100%;
    height: 50px;
    margin-top: 18px;
    border-radius: 999px;
  }
  
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.25s ease;
  }
  
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
  }
  
  @keyframes skeleton-shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
  
  @media (min-width: 768px) {
    .m-profile-page {
      max-width: 430px;
      margin: 0 auto;
      border-left: 1px solid var(--separator_home);
      border-right: 1px solid var(--separator_home);
    }
  }
  
  @media (max-width: 360px) {
    .m-profile-content {
      padding-left: 12px;
      padding-right: 12px;
    }
  
    .profile-skills-card,
    .skills-skeleton {
      padding: 16px;
    }
  
    .section-title {
      font-size: 17px;
    }
  }
  </style>
  